import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FiCheck } from "react-icons/fi";
import PageHeader from "../components/PageHeader";
import facilityEntrance from "../assets/photos/facility-entrance.jpeg";
import "./About.css";

const values = [
  {
    title: "Origin honesty",
    body: "Every lot carries its growing-belt story. No blending secrets, no vague 'Indian origin' labels.",
  },
  {
    title: "Quality without flinching",
    body: "If a batch fails our own sample check, it never reaches your port. Reputation is slower to rebuild than to earn.",
  },
  {
    title: "One accountable partner",
    body: "Sourcing, grading, packing, documentation, freight — held together by one team, one relationship.",
  },
  {
    title: "Long-term, not transactional",
    body: "We're building buyers, not closing orders. Our repeat-buyer rate is the number we care about.",
  },
];

function About() {
  return (
    <motion.main
      className="page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageHeader
        eyebrow="About Agronexus"
        title="A trading house that still <em>thinks like a farmer.</em>"
        subtitle="We started Agronexus to close the quiet gap that most agro trade falls into — the gap between what a farmer knows about his crop, and what a buyer abroad can actually verify. We close it by showing up on both ends."
      />

      <section className="section">
        <div className="container about__grid">
          <div>
            <span className="eyebrow">Our story</span>
            <h2>From Gujarat's fields <em>to the world's tables.</em></h2>
          </div>
          <div className="about__story">
            <p>
              Agronexus Trading Pvt. Ltd. was founded by Ramchandra Bishnoi
              with roots in Kutch's agricultural belt — growing up around
              cumin drying yards, coriander mandis, and the quiet rhythm of
              harvest-to-shipment. What began as sourcing for a handful of
              overseas buyers has grown into an import–export and logistics
              operation serving importers, processors, and retailers across
              multiple countries.
            </p>
            <p>
              We remain deliberately small at the top. Orders are handled by
              people who know the crop, not by a call centre. Every container
              that leaves our warehouse is one our team has seen, sampled,
              and signed off on.
            </p>
          </div>
        </div>
      </section>

      <section className="section about__values-section">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">What we stand for</span>
              <h2>Four things we <em>don't compromise on.</em></h2>
            </div>
          </div>
          <div className="about__values">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                className="about__value"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <span className="about__value-num">0{i + 1}</span>
                <h3>{v.title}</h3>
                <p>{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section about__promise">
        <div className="container about__promise-inner">
          <div className="about__promise-text">
            <span className="eyebrow">The promise</span>
            <h2>What buyers get, <em>every single time.</em></h2>
            <ul className="about__promise-list">
              <li><FiCheck /> Pre-shipment samples on every order</li>
              <li><FiCheck /> Lab-tested moisture, purity, pesticide residues</li>
              <li><FiCheck /> Transparent grading with photographs</li>
              <li><FiCheck /> Shipment documentation ready before loading</li>
              <li><FiCheck /> A real person on email, WhatsApp, or a call</li>
              <li><FiCheck /> Post-shipment support until cargo clears destination</li>
            </ul>
            <Link to="/contact" className="btn btn-primary">
              Work with us
            </Link>
          </div>
          <div className="about__promise-visual">
            <img
              src={facilityEntrance}
              alt="Agronexus Trading Pvt. Ltd. facility entrance, Mothla, Kutch"
            />
            <div className="about__promise-badge">
              <strong>10+</strong>
              <span>Years of export experience</span>
            </div>
          </div>
        </div>
      </section>
    </motion.main>
  );
}

export default About;
